/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import i18next from 'i18next';
import { createFOAsyncThunk } from '../../helpers/thunk.helpers';
import draftApi from '../../../api/draft.api';
import { selectUserData } from '../user/userAccounts/userAccounts.slice';
import history from '../../../history';
import { RoutePath } from '../../../shared/enums/RoutePath';
import { showAlertSuccess } from '../alerts/alerts.slice';
import { createMessage } from '../alerts/alerts.helper';
import { selectSubmissionData } from './submission.selectors';
import { getValidationsThunk } from './submission.thunk.validations';
import { OperationType } from '../../../shared/enums/OperationType';
import { attachmentsFieldEventSuffix } from '../../../components/draft/attachments/services/attachments.helper';

/**
 * Signature saving thunk that can be dispatched with:
 *  submission arg - when the state submission needs to be updated
 *  uiSchema arg - when the validation needs to be called
 *  no submission arg data - when the submission state data is used for saving
 *  isSaveAndClose flag arg - when the history should move to search
 */
export const putUserSignatureThunk = createFOAsyncThunk('submission/putUserSignature', async (args, thunkApi) => {
  const _user = selectUserData(thunkApi.getState());
  const _submission = args?.submission ?? selectSubmissionData(thunkApi.getState());
  const _signatureItems = _submission?.signatures?.signatureItems ?? [];
  const _signature = _signatureItems?.find(item => item?.signer?.id === _user?.id);

  let submission;

  if (_signature) {
    submission = await draftApi.addSignature(_submission.id, _signature);
  } else {
    // there is no signature to save
    submission = _submission;
  }

  thunkApi.dispatch(showAlertSuccess(createMessage(i18next.t('draft.saved.successfully'))));

  if (args?.isSaveAndClose) {
    const params = {
      procedure: submission?.procedure,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.replace(RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, params));
  } else {
    thunkApi.dispatch(getValidationsThunk({ submission, uiSchema: args.uiSchema }));
  }

  return submission;
});

/**
 * Signature removal thunk that should be dispatched with the following args:
 *  submission - the current submission object
 *  uiSchema - needed to perform validation
 */
export const deleteUserSignatureThunk = createFOAsyncThunk('submission/deleteUserSignature', async (args, thunkApi) => {
  const submission = await draftApi.removeSignature(args.submission.id);

  thunkApi.dispatch(showAlertSuccess(createMessage(i18next.t('draft.saved.successfully'))));
  thunkApi.dispatch(getValidationsThunk({ submission, uiSchema: args.uiSchema }));

  return submission;
});

/**
 * Signatures section changed thunk that handles signatures data changes.
 * formData - whole submission object
 * changeProps - section change object
 * uiSchema - used for the validation call
 */
export const postOnSectionChangeThunk = createFOAsyncThunk(
  'submission/postOnSectionChangeThunk',
  async (args, thunkApi) => {
    const { formData, changeProps, uiSchema } = args;

    if (changeProps.sectionName) {
      // section level change
      if (!changeProps.sectionItemId) {
        // non submitted entity canceled (no post-processing here)
        thunkApi.dispatch(getValidationsThunk({ submission: formData, uiSchema }));
        return formData;
      }

      const { sectionName, sectionOperation, sectionItemId } = changeProps;
      const event = `${sectionName}_${sectionOperation}`;
      const submission = await draftApi.postProcessBusinessRules(formData, event, sectionItemId);

      if (sectionName === 'signatureItems') {
        switch (sectionOperation) {
          case OperationType.SUBMIT:
            thunkApi.dispatch(putUserSignatureThunk({ submission, uiSchema }));
            break;
          case OperationType.DELETE:
            thunkApi.dispatch(deleteUserSignatureThunk({ submission, uiSchema }));
            break;
          default:
            thunkApi.dispatch(getValidationsThunk({ submission, uiSchema }));
            break;
        }
      } else {
        thunkApi.dispatch(getValidationsThunk({ submission, uiSchema }));
      }
      return submission;
    }

    // field level change (for fields that trigger postprocessing)
    const { propertyId, value } = changeProps;
    const event = propertyId.endsWith(attachmentsFieldEventSuffix) ? propertyId : `${propertyId}_change`;
    const id = propertyId.endsWith(attachmentsFieldEventSuffix) ? value?.[0]?.id : propertyId;
    const submission = await draftApi.postProcessBusinessRules(formData, event, id);
    thunkApi.dispatch(getValidationsThunk({ submission, uiSchema }));
    return submission;
  },
);

/**
 * Sends the current submission to packaging.
 */
export const postSendToSignAndPackThunk = createFOAsyncThunk(
  'submission/postSendToSignAndPack',
  async (args, thunkApi) => {
    const _submission = selectSubmissionData(thunkApi.getState());
    await draftApi.submitSignatures(_submission.id);
    history.replace(RoutePath.generateRoutePath(RoutePath.DASHBOARD, {}), { showProcessingDialog: true });
  },
);

/**
 * Sends the current submission to packaging.
 */
export const getResendSigningEmailsThunk = createFOAsyncThunk(
  'submission/getResendSigningEmailsThunk',
  async (args, thunkApi) => {
    await draftApi.resendSigningEmails(args.submissionId);
    thunkApi.dispatch(showAlertSuccess(createMessage(i18next.t('submission.sign.sent.emails.success'))));
  },
);
